import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Globe, Pencil, Plus, Trash2, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { InlineEditorShell } from "@/components/InlineEditorShell";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { EmptyState, PageHeader } from "@/components/uiext";
import { SectionHelp } from "@/lib/help.jsx";
import { useConfirm } from "@/lib/confirm.jsx";
import { toast } from "sonner";

export default function DomainsPage() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ domain: "", tenant_id: "", description: "" });
  // Phase M — inline edit state. Only one row can be edited at a time;
  // we keep the draft locally so the user can cancel without round-trip.
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ domain: "", tenant_id: "", description: "" });
  const [savingEdit, setSavingEdit] = useState(false);

  const startEdit = (d) => {
    setEditId(d.id);
    setEditForm({
      domain: d.domain || "",
      tenant_id: d.tenant_id || "",
      description: d.description || "",
    });
  };
  const cancelEdit = () => {
    setEditId(null);
    setEditForm({ domain: "", tenant_id: "", description: "" });
  };
  const saveEdit = async (id) => {
    setSavingEdit(true);
    try {
      // Send tenant_id as null when the user picked "(unassigned)" so the
      // backend explicitly clears the binding (vs. omitting the field).
      await api.patch(`/domains/${id}`, {
        domain: editForm.domain.trim().toLowerCase(),
        tenant_id: editForm.tenant_id || null,
        description: editForm.description,
      });
      toast.success(t("pages.domains.updatedToast", "Domain updated"));
      cancelEdit();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    } finally {
      setSavingEdit(false);
    }
  };

  const load = async () => {
    const [d, tt] = await Promise.all([api.get("/domains"), api.get("/tenants?kind=customer")]);
    setItems(d.data);
    setTenants(tt.data);
  };
  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    try {
      await api.post("/domains", { ...form, tenant_id: form.tenant_id || null });
      toast.success(t("pages.domains.registeredToast"));
      setOpen(false);
      setForm({ domain: "", tenant_id: "", description: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed"));
    }
  };
  const del = async (d) => {
    const _ok = await confirm({ title: t("confirm.deleteDomain", { name: d.domain }), danger: true, confirmLabel: t("common.delete") || "Delete" });
    if (!_ok) return;
    await api.delete(`/domains/${d.id}`);
    load();
  };

  // Phase N+ — 3CX/Vodia-style inline editor takes over the whole page.
  if (open) {
    return (
      <InlineEditorShell
        crumb={t("pages.domains.title")}
        title={t("pages.domains.dialogTitle")}
        onCancel={() => {
          setOpen(false);
          setForm({ domain: "", tenant_id: "", description: "" });
        }}
        onSave={save}
        saveLabel={t("common.register")}
        saveDisabled={!form.domain?.trim()}
        testidRoot="domain-editor"
      >
        <div className="space-y-3">
          <div>
            <Label>{t("fields.domain")}</Label>
            <Input
              value={form.domain}
              onChange={(e) => setForm({ ...form, domain: e.target.value })}
              placeholder="customer1.elswitch.local"
              data-testid="domain-input"
            />
          </div>
          <div>
            <Label>{t("fields.bindToCustomer")}</Label>
            <select
              className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
              value={form.tenant_id}
              onChange={(e) => setForm({ ...form, tenant_id: e.target.value })}
            >
              <option value="">{t("common.none")}</option>
              {tenants.map((tn) => (
                <option key={tn.id} value={tn.id}>
                  {tn.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <Label>{t("fields.description")}</Label>
            <Input
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </div>
        </div>
      </InlineEditorShell>
    );
  }

  return (
    <div data-testid="page-domains">
      <PageHeader
        title={t("pages.domains.title")}
        subtitle={t("pages.domains.subtitle")}
        icon={Globe}
        actions={
          <Button onClick={() => setOpen(true)} data-testid="new-domain-btn">
            <Plus className="h-4 w-4 mr-1" /> {t("pages.domains.newBtn")}
          </Button>
        }
      />
      {items.length === 0 ? (
        <EmptyState
          title={t("pages.domains.emptyTitle")}
          description={t("pages.domains.emptyDesc")}
          action={
            <Button onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4 mr-1" /> {t("pages.domains.emptyAction")}
            </Button>
          }
        />
      ) : (
        <div className="rounded-lg border border-border bg-card overflow-x-auto">
          <Table>
            <TableHeader className="sticky-table-head">
              <TableRow>
                <TableHead>{t("fields.domain")}</TableHead>
                <TableHead>{t("pages.domains.colBoundTenant")}</TableHead>
                <TableHead>{t("pages.domains.colDescription")}</TableHead>
                <TableHead className="w-[100px] text-right">{t("common.actions")}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((d) => {
                const editing = editId === d.id;
                if (editing) {
                  return (
                    <TableRow
                      key={d.id}
                      className="row-hover bg-muted/40"
                      data-testid={`domain-row-edit-${d.id}`}
                    >
                      <TableCell>
                        <Input
                          value={editForm.domain}
                          onChange={(e) =>
                            setEditForm({ ...editForm, domain: e.target.value })
                          }
                          className="font-mono text-sm h-8"
                          data-testid={`domain-edit-domain-${d.id}`}
                          autoFocus
                        />
                      </TableCell>
                      <TableCell>
                        <Select
                          value={editForm.tenant_id || "__none__"}
                          onValueChange={(v) =>
                            setEditForm({
                              ...editForm,
                              tenant_id: v === "__none__" ? "" : v,
                            })
                          }
                        >
                          <SelectTrigger
                            className="h-8 text-sm"
                            data-testid={`domain-edit-tenant-${d.id}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">
                              {t("pages.domains.unassigned", "(unassigned)")}
                            </SelectItem>
                            {tenants.map((tn) => (
                              <SelectItem key={tn.id} value={tn.id}>
                                {tn.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Input
                          value={editForm.description}
                          onChange={(e) =>
                            setEditForm({ ...editForm, description: e.target.value })
                          }
                          className="text-sm h-8"
                          data-testid={`domain-edit-desc-${d.id}`}
                          placeholder={t("pages.domains.colDescription")}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-[hsl(var(--ok))]"
                            onClick={() => saveEdit(d.id)}
                            disabled={savingEdit || !editForm.domain.trim()}
                            data-testid={`domain-save-${d.id}`}
                            title={t("common.save", "Save")}
                          >
                            <Check className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={cancelEdit}
                            disabled={savingEdit}
                            data-testid={`domain-cancel-${d.id}`}
                            title={t("common.cancel", "Cancel")}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                }
                return (
                  <TableRow key={d.id} className="row-hover" data-testid={`domain-row-${d.id}`}>
                    <TableCell className="font-mono text-sm">{d.domain}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {tenants.find((tn) => tn.id === d.tenant_id)?.name || "—"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {d.description || "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => startEdit(d)}
                          data-testid={`domain-edit-${d.id}`}
                          title={t("common.edit", "Edit")}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-[hsl(var(--danger))]"
                          onClick={() => del(d)}
                          data-testid={`domain-delete-${d.id}`}
                          title={t("common.delete", "Delete")}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
